import {
  ArmouryEntry, BalanceData, ChestResult, GearItem, OfferScreen, SkinData, SupplyId, TrophyHallJson, TrophyHallState, armouryBlock, armouryShelf, armourySupplies,
  armouryWeek, buyArmoury, buyTrophySkin, isoWeekNumber, purchasesAllowed, trialEndsAt, trophyHallOpen, trophySkins, utcDay,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";
import { HasSections, registerSaveSection } from "./saveSections";

/**
 * The Trophy Hall's use cases (MP-23, docs/SHOP.md §9.7; domain meta/trophyHall.ts): the Treasures (cosmetics bought
 * once with Trophies, granted through the cosmetics' own unlock, idempotent by skin id) and the Armoury (the weekly
 * shelf, per-entry weekly caps, a chest banked with its gear, supplies shown before buying). Trophies are earned only
 * (event rewards, app events.ts / eventModes.ts / modes.ts); nothing here takes money. Hub only, like every purchase
 * (the never list). The state lives in the `trophies` save section.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const trophiesSection = registerSaveSection<TrophyHallJson>("trophies", asObject, (v) => v);

export function trophyHallFromSave(save: HasSections): TrophyHallState { return TrophyHallState.fromJson(trophiesSection.read(save)); }

/** One Treasure as the page draws it. */
export interface TreasureView {
  readonly skin: SkinData;
  readonly price: number;
  readonly owned: boolean;
  /** Why it cannot be bought now ("" when it can): "owned", "not enough", "not saving here". */
  readonly blocked: string;
}

/** One Armoury entry as the page draws it. */
export interface ArmourySlotView {
  readonly entry: ArmouryEntry;
  /** Bought this week (the next purchase's number, as `buyArmoury` asks for it). */
  readonly bought: number;
  /** Left this week ("Left: n"). */
  readonly left: number;
  /** Under the entry's level: drawn locked with it. */
  readonly locked: boolean;
  /** The next purchase's supplies (a supplies pack; shown before buying), else empty. */
  readonly supplies: readonly SupplyId[];
  /** Why it cannot be bought now ("" when it can). */
  readonly blocked: string;
}

export interface TrophyHallView {
  readonly open: boolean;
  /** Trophies held. */
  readonly balance: number;
  /** The Armoury's UTC week and its ISO week number (the shelf's label). */
  readonly week: number;
  readonly isoWeek: number;
  /** When the shelf restocks (Monday 00:00 UTC). */
  readonly endsAt: number;
  readonly treasures: readonly TreasureView[];
  readonly armoury: readonly ArmourySlotView[];
}

export interface ArmouryBuy {
  readonly entry: ArmouryEntry;
  readonly supplies: readonly SupplyId[];
  /** Essence and sigils paid (0 / null for none). */
  readonly essence: number;
  readonly sigil: number | null;
  readonly sigils: number;
  /** The chest it paid with its banked gear (the chest reveal plays it). */
  readonly opened: { readonly chest: ChestResult; readonly gear: readonly GearItem[] } | null;
}

export class TrophyHall {
  constructor(private readonly catalog: LevelCatalog, readonly state: TrophyHallState) {}

  private get balance(): BalanceData { return this.catalog.balance; }
  private get level(): number { return Math.max(1, this.catalog.highestLevelCleared); }
  get trophies(): number { return this.catalog.economy.wallet.trophies; }

  /** Notes the first Trophies held (the Hall then stays open). True the moment that happens (the caller saves). */
  noteMet(): boolean {
    if (this.state.met || this.trophies <= 0) return false;
    this.state.met = true;
    return true;
  }

  /** Open from its level, or as soon as the player has held Trophies. */
  get open(): boolean { return trophyHallOpen(this.balance, this.catalog.highestLevelCleared, this.trophies, this.state.met); }

  view(now: number): TrophyHallView {
    const week = armouryWeek(now), w = this.catalog.economy.wallet, saving = this.catalog.canPersist();
    const t = { profile: this.catalog.profile, wallet: w, level: this.level };
    const treasures = trophySkins(this.balance).map(({ skin, price }): TreasureView => {
      const owned = this.catalog.ownsSkin(skin.id);
      return { skin, price, owned, blocked: owned ? "owned" : w.trophies < price ? "not enough" : !saving ? NOT_SAVING_HERE : "" };
    });
    const armoury = armouryShelf(this.balance, week).map((entry): ArmourySlotView => {
      const bought = this.state.boughtOf(entry.id, week), b = armouryBlock(this.balance, this.state, entry, week, t);
      return {
        entry, bought, left: Math.max(0, entry.weeklyCap - bought), locked: this.level < entry.minLevel,
        supplies: armourySupplies(this.balance, entry, week, bought), blocked: b === "" && !saving ? NOT_SAVING_HERE : b,
      };
    });
    return { open: this.open, balance: w.trophies, week, isoWeek: isoWeekNumber(utcDay(now)), endsAt: trialEndsAt(week), treasures, armoury };
  }

  /** Buys a Treasure (hub only): spends its Trophies once and unlocks the skin (owned and new). True or why not. */
  buyTreasure(skinId: string, now: number, screen: OfferScreen = "hub"): true | string {
    const refused = this.refusal(screen);
    if (refused) return refused;
    const r = buyTrophySkin(this.balance, this.catalog.economy.wallet, skinId, (id) => this.catalog.ownsSkin(id), now);
    if (r === true) this.catalog.unlockSkin(skinId);
    return r;
  }

  /** Buys the `n`th (as shown) purchase of Armoury entry `id` this week (hub only); a chest is banked with its gear. */
  buyArmoury(id: string, n: number, now: number, screen: OfferScreen = "hub"): ArmouryBuy | string {
    const refused = this.refusal(screen);
    if (refused) return refused;
    const c = this.catalog;
    const r = buyArmoury(this.balance, this.state, id, n, now, { profile: c.profile, wallet: c.economy.wallet, playerId: c.playerId, level: this.level });
    if (typeof r === "string") return r;
    const p = r.paid, chest = p?.chest ?? null;
    return {
      entry: r.entry, supplies: r.supplies, essence: p?.essence ?? 0, sigil: p?.sigil ?? null, sigils: p?.sigils ?? 0,
      opened: chest ? { chest, gear: c.bankChest(chest) } : null,
    };
  }

  private refusal(screen: OfferScreen): string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.open) return "locked";
    return this.catalog.canPersist() ? "" : NOT_SAVING_HERE;
  }
}
